import axios from 'axios';
import type { ApiErrorResponse, ApiValidationDetail } from '@/types/api';

/**
 * Message used when the request never reached the server (or no response came back)
 */
export const NETWORK_ERROR_MESSAGE = 'Unable to reach the server. Check your connection and try again.';

/**
 * Message used when the request timed out
 */
export const TIMEOUT_ERROR_MESSAGE = 'The server took too long to respond. Please try again.';

interface ApiErrorInit {
  status?: number;
  code?: string;
  serverMessage?: string;
  data?: unknown;
  isNetworkError?: boolean;
  isTimeout?: boolean;
  isCancelled?: boolean;
  cause?: unknown;
}

/**
 * Normalised API error.
 *
 * Created by the axios response interceptor for every failed request so that
 * the rest of the app never has to know about AxiosError. `serverMessage`
 * carries the human-readable message the control plane sent (if any).
 */
export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly serverMessage?: string;
  readonly data?: unknown;
  readonly isNetworkError: boolean;
  readonly isTimeout: boolean;
  readonly isCancelled: boolean;

  constructor(message: string, init: ApiErrorInit = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.serverMessage = init.serverMessage;
    this.data = init.data;
    this.isNetworkError = init.isNetworkError ?? false;
    this.isTimeout = init.isTimeout ?? false;
    this.isCancelled = init.isCancelled ?? false;
    if (init.cause !== undefined) {
      (this as { cause?: unknown }).cause = init.cause;
    }
  }
}

/**
 * Type guard for ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Whether the error represents a cancelled request (AbortController / unmount)
 */
export function isCancelledError(error: unknown): boolean {
  return (isApiError(error) && error.isCancelled) || axios.isCancel(error);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/**
 * Extract field-level messages from a validation error `details` array
 */
function extractDetailMessages(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details
    .map((detail: ApiValidationDetail | string) => {
      if (typeof detail === 'string') return nonEmptyString(detail);
      if (detail && typeof detail === 'object') return nonEmptyString(detail.message);
      return undefined;
    })
    .filter((message): message is string => message !== undefined);
}

/**
 * Extract the human-readable message from a control plane error body.
 *
 * Order: `error`, then `message`, then plain-text bodies (not HTML pages from
 * a proxy). Validation `details` messages are appended after the main message.
 */
export function extractServerMessage(data: unknown): string | undefined {
  if (typeof data === 'string') {
    const text = nonEmptyString(data);
    // Ignore HTML error pages (e.g. 502 from a reverse proxy) and huge bodies
    if (!text || text.startsWith('<') || text.length > 300) return undefined;
    return text;
  }

  if (!data || typeof data !== 'object') return undefined;

  const body = data as ApiErrorResponse;
  const main = nonEmptyString(body.error) ?? nonEmptyString(body.message);
  const details = extractDetailMessages(body.details);

  if (!main) return details.length > 0 ? details.join('; ') : undefined;
  return details.length > 0 ? `${main}: ${details.join('; ')}` : main;
}

/**
 * Convert anything thrown by axios (or already normalised) into an ApiError.
 * Idempotent: an ApiError is returned unchanged.
 */
export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) return error;

  if (axios.isCancel(error)) {
    return new ApiError('Request cancelled', { isCancelled: true, cause: error });
  }

  if (axios.isAxiosError(error)) {
    const response = error.response;

    if (!response) {
      const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      return new ApiError(isTimeout ? TIMEOUT_ERROR_MESSAGE : NETWORK_ERROR_MESSAGE, {
        code: error.code,
        isNetworkError: !isTimeout,
        isTimeout,
        cause: error,
      });
    }

    const serverMessage = extractServerMessage(response.data);
    const body = response.data as ApiErrorResponse | undefined;
    const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : undefined;

    return new ApiError(serverMessage ?? `Request failed with status ${response.status}`, {
      status: response.status,
      code,
      serverMessage,
      data: response.data,
      cause: error,
    });
  }

  if (error instanceof Error) {
    return new ApiError(error.message, { cause: error });
  }

  return new ApiError('Unknown error', { cause: error });
}

/**
 * Get a user-facing message for an error thrown by an API call.
 *
 * Uses the server-provided message when there is one, a network/timeout
 * message when the server could not be reached, and `fallback` otherwise.
 * Non-API errors (programming errors) always yield `fallback` so that raw
 * technical messages never leak into the UI.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isApiError(error) && !axios.isAxiosError(error)) {
    return fallback;
  }

  const apiError = toApiError(error);
  if (apiError.serverMessage) return apiError.serverMessage;
  if (apiError.isTimeout) return TIMEOUT_ERROR_MESSAGE;
  if (apiError.isNetworkError) return NETWORK_ERROR_MESSAGE;
  return fallback;
}
